import { h as __h } from "preact";
import { signal } from "@preact/signals";
import { Component, h } from "preact";
/** @import { TargetedMouseEvent } from "preact"; */

import { adminMachineResponse, adminMemberResponse, groupResponse } from "~/backend/validate.js";
import { query, paginatedResponse, response } from "~/lib/api.js";
import { identity } from "~/lib/identity.js";
import html from "~/util/html.js";
import { pluralize } from "~/util/pluralize.js";
import { navigate } from "~/util/router.js";
import Title from "~/util/title.js";
import Button from "~/component/Button.js";
import Data from "~/component/Data.js";
import Icon from "~/component/Icon.js";
import Loader from "~/component/Loader.js";
import { Crumb } from "~/admin/component/Breadcrumbs.js";
import MappingsLock from "~/admin/component/MappingsLock.js";
import ObjectPage, { ObjectPageSection } from "~/admin/component/ObjectPage.js";
import Pagination from "~/admin/component/Pagination.js";
import ComputerTable from "~/admin/res/computer/ComputerTable.js";
import { addComputers } from "~/admin/res/group/actions/addComputers.js";
import { addMembers } from "~/admin/res/group/actions/addMembers.js";
import { deleteGroup } from "~/admin/res/group/actions/deleteGroup.js";
import { removeComputers } from "~/admin/res/group/actions/removeComputers.js";
import { removeMembers } from "~/admin/res/group/actions/removeMembers.js";
import { renameGroup } from "~/admin/res/group/actions/renameGroup.js";
import MemberTable from "~/admin/res/member/MemberTable.js";
import util from "~/style/util.js";
/** @import { orgResponse, sessionEnterpriseResponse } from "~/backend/validate.js"; */

/**
 * @extends {Component<Props>}
 *
 * @typedef {object} Props
 * @property {orgResponse} org
 * @property {sessionEnterpriseResponse} enterprise
 * @property {string} groupId
 */
export default class GroupsDetail extends Component {
  groupLookup = query(() => ({
    key: [`/v2/orgs/${this.props.org.id}/groups/${this.props.groupId}`],
    keepPreviousData: true
  }), {
    schema: response(groupResponse)
  });
  render() {
    const group = this.groupLookup.data?.data;
    return [__h(Crumb, {}, group?.name ?? __h(Loader, {})), group && __h(Title, {
      title: group.name
    }), __h(Data, {
      loading: this.groupLookup.loading && !this.groupLookup.data,
      error: this.groupLookup.error,
      empty: Boolean(this.groupLookup.data) && !group
    }, group && this.renderPage(group))];
  }

  /** @param {groupResponse} group */
  renderPage(group) {
    return __h(ObjectPage, {
      backlink: {
        href: `/org/${this.props.org.id}/groups`,
        name: "Groups"
      },
      title: [__h(Icon, {
        name: "users",
        size: 22
      }), __h("span", {}, group.name)],
      desc: group.description,
      actions: identity.permission(this.props.org.id, "groups", "edit") && [__h(Button, {
        size: "small",
        onClick: () => renameGroup({
          orgId: this.props.org.id
        }, group)
      }, __h(Icon, {
        name: "pencil",
        size: 12
      }), "Rename group"), __h(Button, {
        size: "small",
        kind: "negative",
        onClick: async () => {
          const res = await deleteGroup({
            orgId: this.props.org.id
          }, group);
          if (res) navigate(`/org/${this.props.org.id}/groups`);
        }
      }, __h(Icon, {
        name: "trash",
        size: 12
      }), "Delete group")],
      sidebar: [__h(ObjectPageSection, {
        title: "Members"
      }, __h("span", {}, group.member_count, " ", pluralize(group.member_count, "member"))), __h(ObjectPageSection, {
        title: "Computers"
      }, __h("span", {}, group.machine_count, " ", pluralize(group.machine_count, "computer")))]
    }, __h(GroupMembers, {
      org: this.props.org,
      enterprise: this.props.enterprise,
      groupId: group.id
    }), __h(GroupComputers, {
      org: this.props.org,
      groupId: group.id
    }));
  }
}

/**
 * @extends {Component<GroupMembersProps>}
 *
 * @typedef {object} GroupMembersProps
 * @property {orgResponse} org
 * @property {sessionEnterpriseResponse} enterprise
 * @property {string} groupId
 */
class GroupMembers extends Component {
  checked = signal(/** @type {Map<string, adminMemberResponse>} */new Map());
  paginate = new Pagination.State({
    size: 5,
    local: true
  });
  sort = MemberTable.sort({
    local: true
  });
  members = query(() => ({
    key: [`/ui/admin/orgs/${this.props.org.id}/members`, {
      ...this.paginate.query(),
      ...this.sort.query(),
      group_id: this.props.groupId
    }],
    keepPreviousData: true
  }), {
    schema: paginatedResponse(adminMemberResponse)
  });
  render() {
    const groupId = this.props.groupId;
    const locked = this.props.enterprise.is_group_sync_enabled;
    const canEdit = identity.permission(this.props.org.id, "groups", "edit");
    return __h(ObjectPageSection, {
      title: "Members",
      actions: canEdit && [__h(MappingsLock, {
        locked: locked
      }, __h(Button, {
        size: "small",
        level: "tertiary",
        disabled: locked,
        onClick: (/** @type {TargetedMouseEvent<HTMLButtonElement>} */event) => addMembers({
          anchor: event.currentTarget,
          placement: "bottom-start",
          orgId: this.props.org.id,
          groupId
        })
      }, __h(Icon, {
        name: "plus",
        size: 12
      }), "Add")), __h(MappingsLock, {
        locked: locked
      }, __h(Button, {
        size: "small",
        level: "tertiary",
        kind: "negative",
        disabled: this.checked.value.size === 0 || locked,
        onClick: async () => {
          const res = await removeMembers({
            orgId: this.props.org.id,
            groupId
          }, ...this.checked.value.values());
          if (res) this.checked.value = new Map();
        }
      }, __h(Icon, {
        name: "trash",
        size: 12
      }), "Remove"))]
    }, __h("div", {
      "class": util.card
    }, h(MemberTable, {
      orgId: this.props.org.id,
      paginate: this.paginate,
      sort: this.sort,
      query: this.members,
      checked: canEdit ? this.checked : undefined,
      headers: h => h.filter(h => h.id !== "groups" && h.id !== "computers"),
      footer: __h("a", {
        href: "/org/" + this.props.org.id + "/members?group_id=" + groupId
      }, "Show/edit all results"),
      onTripleDots: (items, member) => [...items.filter(item => item.key !== "remove"), ...(canEdit ? [{
        icon: "trash",
        text: "Remove from group",
        kind: (/** @type {const} */"negative"),
        disabled: locked,
        tooltip: locked && "Managed by directory mappings",
        onSelect: async () => {
          const res = await removeMembers({
            orgId: this.props.org.id,
            groupId
          }, member);
          if (res) {
            const checked = new Map(this.checked.value);
            checked.delete(member.id);
            this.checked.value = checked;
          }
        }
      }] : [])],
      scroll: false,
      mappingsLock: locked
    })));
  }
}

/**
 * @extends {Component<GroupComputersProps>}
 *
 * @typedef {object} GroupComputersProps
 * @property {orgResponse} org
 * @property {string} groupId
 */
class GroupComputers extends Component {
  checked = signal(/** @type {Map<string, adminMachineResponse>} */new Map());
  paginate = new Pagination.State({
    size: 5,
    local: true
  });
  sort = ComputerTable.sort({
    local: true
  });
  computers = query(() => ({
    key: [`/ui/admin/orgs/${this.props.org.id}/machines`, {
      ...this.paginate.query(),
      ...this.sort.query(),
      group_id: this.props.groupId
    }],
    keepPreviousData: true
  }), {
    schema: paginatedResponse(adminMachineResponse)
  });
  render() {
    const groupId = this.props.groupId;
    // Group computers are assigned through the machines API
    const canEdit = identity.permission(this.props.org.id, "machines", "edit");
    return __h(ObjectPageSection, {
      title: "Computers",
      actions: canEdit && [__h(Button, {
        size: "small",
        level: "tertiary",
        onClick: (/** @type {TargetedMouseEvent<HTMLButtonElement>} */event) => addComputers({
          anchor: event.currentTarget,
          placement: "bottom-start",
          orgId: this.props.org.id,
          groupId
        })
      }, __h(Icon, {
        name: "plus",
        size: 12
      }), "Add"), __h(Button, {
        size: "small",
        level: "tertiary",
        kind: "negative",
        disabled: this.checked.value.size === 0,
        onClick: async () => {
          const res = await removeComputers({
            orgId: this.props.org.id,
            groupId
          }, ...this.checked.value.values());
          if (res) this.checked.value = new Map();
        }
      }, __h(Icon, {
        name: "trash",
        size: 12
      }), "Remove")]
    }, __h("div", {
      "class": util.card
    }, h(ComputerTable, {
      orgId: this.props.org.id,
      paginate: this.paginate,
      sort: this.sort,
      query: this.computers,
      checked: canEdit ? this.checked : undefined,
      headers: h => h.filter(h => h.id !== "assignedTo" && h.id !== "location"),
      footer: __h("a", {
        href: "/org/" + this.props.org.id + "/computers?group_id=" + groupId
      }, "Show/edit all results"),
      onTripleDots: (items, machine) => items.map(item => item.key === "remove" ? {
        ...item,
        text: "Remove from group",
        onSelect: async () => {
          const res = await removeComputers({
            orgId: this.props.org.id,
            groupId
          }, machine);
          if (res) {
            const checked = new Map(this.checked.value);
            checked.delete(machine.id);
            this.checked.value = checked;
          }
        }
      } : item),
      scroll: false
    })));
  }
}